import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, TrendingUp, Wallet, Zap, ChevronDown, ChevronUp, Info, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { cardStyle, cardInnerStyle } from '../utils/cardStyle';
import { useSocket } from '../context/SocketContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_COLORS = {
  Elite:      '#f59e0b',
  Advanced:   '#6366f1',
  Standard:   '#16C784',
  Restricted: '#f97316',
  Minimum:    '#ef4444',
};

function fmt(n) {
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)   return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

const BUCKET_COLORS = { pre_trade: '#6366f1', execution: '#16C784', risk: '#f59e0b', outcome: '#38bdf8' };

// Red → Yellow → Green gradient slider
function GradientBar({ value, max = 100, label, sublabel }) {
  const { colors } = useTheme();
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px]" style={{ color: colors.secondaryText }}>{label}</span>
        {sublabel && (
          <span className="text-[11px] font-semibold tabular-nums" style={{ color: colors.primaryText }}>
            {sublabel}
          </span>
        )}
      </div>
      <div className="relative h-1.5 rounded-full overflow-visible"
        style={{ background: 'linear-gradient(to right, #ef4444 0%, #f97316 25%, #f59e0b 50%, #84cc16 75%, #16C784 100%)' }}
      >
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-sm"
          style={{ left: `calc(${pct}% - 6px)`, backgroundColor: '#fff', transition: 'left 0.4s ease' }}
        />
      </div>
    </div>
  );
}

function bucketInsight(b) {
  // Find the weakest and strongest sub-component
  const entries = Object.entries(b.components);
  const worst = entries.reduce((a, c) => c[1] < a[1] ? c : a, entries[0]);
  const best  = entries.reduce((a, c) => c[1] > a[1] ? c : a, entries[0]);
  if (worst[1] < 45) return { text: worst[0], positive: false };
  if (best[1] >= 80) return { text: best[0], positive: true };
  return null;
}

function ScoreBuckets({ buckets }) {
  const { colors } = useTheme();
  if (!buckets) return null;
  const entries = Object.entries(buckets);
  return (
    <div
      className="rounded-xl px-3 py-2.5"
      style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.border}` }}
    >
      <p className="text-[10px] uppercase tracking-wide font-medium mb-2.5" style={{ color: colors.secondaryText }}>
        Why your score is this
      </p>
      <div className="grid grid-cols-2 gap-2">
        {entries.map(([key, b]) => {
          const color = BUCKET_COLORS[key] || colors.accent;
          const scoreColor = b.score >= 65 ? '#16C784' : b.score >= 50 ? color : '#ef4444';
          const insight = bucketInsight(b);
          return (
            <div
              key={key}
              className="rounded-lg px-2.5 py-2 flex flex-col gap-1"
              style={{ backgroundColor: `${scoreColor}10`, border: `1px solid ${scoreColor}25` }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium leading-tight" style={{ color: colors.secondaryText }}>
                  {b.label.replace(' Planning','').replace(' Discipline','').replace(' Context','')}
                </span>
                <span className="text-[10px]" style={{ color: colors.secondaryText }}>{b.weight}%</span>
              </div>
              <span className="text-[22px] font-bold tabular-nums leading-none" style={{ color: scoreColor }}>
                {b.score}
              </span>
              {insight && (
                <span className="text-[10px] leading-tight" style={{ color: insight.positive ? '#16C784' : '#f97316' }}>
                  {insight.positive ? '↑ ' : '↓ '}{insight.text}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CapitalWidget({ compact = false, virtualBalance = null }) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { onBalanceUpdated, offBalanceUpdated } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/capital/status`);
      setData(res.data);
    } catch (e) {
      // silent — widget hides if API fails
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    onBalanceUpdated(fetch);
    return () => offBalanceUpdated(fetch);
  }, [fetch, onBalanceUpdated, offBalanceUpdated]);

  if (loading || !data) return null;

  const {
    total_capital, active_capital, locked_capital, available_capital,
    capital_behavior_score, bootstrap_mode, trades_until_live,
    tier, tomorrow, score_buckets,
  } = data;

  const tierColor = TIER_COLORS[tier] || colors.accent;
  const scoreColor =
    capital_behavior_score >= 65 ? '#16C784' :
    capital_behavior_score >= 50 ? colors.accent :
    '#ef4444';

  // ── Compact header variant ──────────────────────────────────────────────
  if (compact) {
    const tiles = [
      { label: 'Locked',       value: fmt(locked_capital),    color: locked_capital > 0 ? '#f97316' : colors.secondaryText, bg: locked_capital > 0 ? '#f9731610' : `${colors.accent}08`, border: locked_capital > 0 ? '#f9731628' : colors.border },
      { label: 'Available',    value: fmt(available_capital), color: colors.profit,  bg: `${colors.accent}08`, border: colors.border },
      { label: 'Active Limit', value: fmt(active_capital),    color: tierColor,      bg: `${tierColor}10`,     border: `${tierColor}28` },
    ];
    return (
      <div style={cardStyle(colors.cardBg, tierColor, isDark)}>
        <div className="rounded-2xl overflow-hidden" style={cardInnerStyle(isDark)}>

        {/* Header */}
        <div className="flex items-center justify-between px-3 pt-2.5 pb-2 rounded-t-2xl" style={{ backgroundColor: colors.cardHeaderBg, borderBottom: `1px solid ${colors.cardHeaderBorder}` }}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tierColor}18` }}>
              <Wallet className="w-3.5 h-3.5" style={{ color: tierColor }} />
            </div>
            <span className="text-[13px] font-semibold tracking-wider uppercase" style={{ color: colors.secondaryText }}>Capital Control</span>
          </div>
          <button onClick={() => navigate('/capital')} style={{ color: colors.secondaryText }}>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="px-3 pt-2 pb-3 flex flex-col gap-2">

        {/* 4-col stat row: Available | Active Limit | Locked | Tomorrow */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          <div className="rounded-lg px-2 py-1.5 col-span-1" style={{ backgroundColor: `${tierColor}12`, border: `1px solid ${tierColor}30` }}>
            <p className="text-[9px] uppercase tracking-wide mb-0.5" style={{ color: tierColor }}>Available</p>
            <p className="text-[13px] font-bold tabular-nums leading-none" style={{ color: tierColor }}>{fmt(available_capital)}</p>
          </div>
          <div className="rounded-lg px-2 py-1.5 col-span-1" style={{ backgroundColor: `${tierColor}10`, border: `1px solid ${tierColor}28` }}>
            <p className="text-[9px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Limit</p>
            <p className="text-[13px] font-bold tabular-nums leading-none" style={{ color: tierColor }}>{fmt(active_capital)}</p>
          </div>
          <div className="rounded-lg px-2 py-1.5 col-span-1" style={{ backgroundColor: locked_capital > 0 ? '#f9731610' : `${colors.accent}08`, border: `1px solid ${locked_capital > 0 ? '#f9731628' : colors.border}` }}>
            <p className="text-[9px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Locked</p>
            <p className="text-[13px] font-bold tabular-nums leading-none" style={{ color: locked_capital > 0 ? '#f97316' : colors.secondaryText }}>{fmt(locked_capital)}</p>
          </div>
          {tomorrow && !bootstrap_mode ? (
            <div className="rounded-lg px-2 py-1.5 col-span-1" style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.border}` }}>
              <p className="text-[9px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Tomorrow</p>
              <p className="text-[13px] font-bold tabular-nums leading-none"
                style={{ color: tomorrow.projected_capital >= active_capital ? '#16C784' : '#ef4444' }}>
                {fmt(tomorrow.projected_capital)}
              </p>
            </div>
          ) : (
            <div className="rounded-lg px-2 py-1.5 col-span-1" style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.border}` }}>
              <p className="text-[9px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Score</p>
              <p className="text-[13px] font-bold tabular-nums leading-none" style={{ color: colors.accent }}>{capital_behavior_score}</p>
            </div>
          )}
        </div>

        {/* Progress bar — same style as TraderPerformancePortal MiniBar */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <p className="text-[9px]" style={{ color: colors.secondaryText }}>Available quota</p>
            <p className="text-[9px] font-semibold tabular-nums" style={{ color: tierColor }}>
              {active_capital > 0 ? Math.round((available_capital / active_capital) * 100) : 0}% of {fmt(active_capital)}
            </p>
          </div>
          <div className="relative w-full" style={{ height: 6 }}>
            <div className="absolute inset-0 rounded-full overflow-hidden" style={{ backgroundColor: colors.progressTrack }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${active_capital > 0 ? Math.min(100, (available_capital / active_capital) * 100) : 0}%`, background: 'linear-gradient(90deg, #EF4444 0%, #F59E0B 30%, #EAB308 55%, #22C55E 80%, #16C784 100%)' }} />
            </div>
            <div className="absolute top-1/2 -translate-y-1/2"
              style={{ left: `calc(${active_capital > 0 ? Math.min(100, (available_capital / active_capital) * 100) : 0}% - 5px)`, width: 10, height: 10, borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 0 4px rgba(0,0,0,0.6)', transition: 'left 0.5s ease', zIndex: 1 }} />
          </div>
        </div>

        {bootstrap_mode && (
          <p className="text-[10px]" style={{ color: colors.accent }}><strong>{trades_until_live}</strong> trades until behavior-capital kicks in</p>
        )}
        </div>{/* end px-3 content */}
        </div>{/* end rounded-2xl */}
      </div>
    );
  }
  // ── End compact ─────────────────────────────────────────────────────────

  const availablePct = active_capital > 0
    ? Math.round((available_capital / active_capital) * 100)
    : 0;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}` }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 pt-2.5 pb-2 rounded-t-xl"
        style={{ backgroundColor: colors.cardHeaderBg, borderBottom: `1px solid ${colors.cardHeaderBorder}` }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded flex items-center justify-center"
            style={{ backgroundColor: `${tierColor}18` }}
          >
            <Wallet className="w-3 h-3" style={{ color: tierColor }} />
          </div>
          <span
            className="text-[13px] font-semibold tracking-wider uppercase"
            style={{ color: colors.secondaryText }}
          >
            Capital Control
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Tier badge */}
          <span
            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: `${tierColor}20`, color: tierColor }}
          >
            {tier}
          </span>
          <button onClick={() => setShowInfo(v => !v)}>
            <Info className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
          </button>
          <button onClick={() => setExpanded(v => !v)}>
            {expanded
              ? <ChevronUp className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
              : <ChevronDown className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
            }
          </button>
        </div>
      </div>

      {/* Info tooltip */}
      {showInfo && (
        <div
          className="px-3 py-2 text-[11px] leading-relaxed"
          style={{ color: colors.secondaryText, backgroundColor: `${colors.accent}08`, borderBottom: `1px solid ${colors.border}` }}
        >
          Your <strong style={{ color: colors.primaryText }}>Active Capital</strong> is set each morning based on your behavior — not your profit. Trade with discipline to unlock more.
          <br />
          <strong style={{ color: colors.primaryText }}>Locked</strong> = open trade value. <strong style={{ color: colors.primaryText }}>Available</strong> = what you can trade today.
        </div>
      )}

      {/* Bootstrap banner */}
      {bootstrap_mode && (
        <div
          className="px-3 py-2 flex items-center gap-2"
          style={{ backgroundColor: `${colors.accent}12`, borderBottom: `1px solid ${colors.border}` }}
        >
          <Zap className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.accent }} />
          <span className="text-[11px]" style={{ color: colors.accent }}>
            <strong>{trades_until_live} trade{trades_until_live !== 1 ? 's' : ''}</strong> remaining before behavior-based capital kicks in
          </span>
        </div>
      )}

      {/* Main stat grid */}
      <div className="px-3 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Available Capital — primary stat */}
        <div
          className="col-span-2 rounded-xl px-3 py-2.5 flex items-center justify-between"
          style={{ backgroundColor: `${tierColor}12`, border: `1px solid ${tierColor}30` }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-wide font-medium mb-0.5" style={{ color: tierColor }}>
              Available Today
            </p>
            <p className="text-[22px] font-bold tabular-nums leading-none" style={{ color: tierColor }}>
              {fmt(available_capital)}
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: colors.secondaryText }}>
              {availablePct}% of ₹{fmt(active_capital)} daily limit
            </p>
          </div>
          {!bootstrap_mode && (
            <div className="flex flex-col items-center">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold"
                style={{ backgroundColor: `${scoreColor}18`, color: scoreColor, border: `2px solid ${scoreColor}40` }}
              >
                {capital_behavior_score}
              </div>
              <p className="text-[9px] mt-0.5 text-center" style={{ color: colors.secondaryText }}>
                score
              </p>
            </div>
          )}
        </div>

        {/* Total Capital */}
        <div
          className="rounded-lg px-2.5 py-2"
          style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.border}` }}
        >
          <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>
            Total Capital
          </p>
          <p className="text-[15px] font-bold tabular-nums" style={{ color: colors.primaryText }}>
            {fmt(total_capital)}
          </p>
          <p className="text-[10px]" style={{ color: colors.secondaryText }}>incl. P&L</p>
        </div>

        {/* Locked Capital */}
        <div
          className="rounded-lg px-2.5 py-2"
          style={{
            backgroundColor: locked_capital > 0 ? '#f9731612' : `${colors.accent}08`,
            border: `1px solid ${locked_capital > 0 ? '#f9731630' : colors.border}`,
          }}
        >
          <div className="flex items-center gap-1 mb-0.5">
            <Lock className="w-2.5 h-2.5" style={{ color: locked_capital > 0 ? '#f97316' : colors.secondaryText }} />
            <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>
              Locked
            </p>
          </div>
          <p
            className="text-[15px] font-bold tabular-nums"
            style={{ color: locked_capital > 0 ? '#f97316' : colors.secondaryText }}
          >
            {fmt(locked_capital)}
          </p>
          <p className="text-[10px]" style={{ color: colors.secondaryText }}>in open trades</p>
        </div>
      </div>

      {/* Expanded section */}
      {expanded && (
        <div
          className="px-3 pb-3 flex flex-col gap-3"
          style={{ borderTop: `1px solid ${colors.border}` }}
        >
          {/* Bar breakdown */}
          <div className="flex flex-col gap-3 pt-3">
            <GradientBar label="Active Capital" value={active_capital} max={200000} sublabel={`${fmt(active_capital)} daily limit`} />
            <GradientBar label="Locked"         value={locked_capital} max={active_capital || 1} sublabel={`${fmt(locked_capital)} in trades`} />
            <GradientBar label="Available"      value={available_capital} max={active_capital || 1} sublabel={`${fmt(available_capital)} to trade`} />
          </div>

          {/* Score breakdown — why is my score what it is */}
          {!bootstrap_mode && <ScoreBuckets buckets={score_buckets} />}

          {/* Tomorrow's capital */}
          {tomorrow && (
            <div
              className="rounded-xl px-3 py-2.5"
              style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.border}` }}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3" style={{ color: colors.accent }} />
                    <div>
                    <span className="text-[11px] font-semibold" style={{ color: colors.primaryText }}>
                      Tomorrow's Limit
                    </span>
                    <p className="text-[10px]" style={{ color: colors.secondaryText }}>resets at 9 AM based on today's behavior</p>
                  </div>
                </div>
                <span
                  className="text-[13px] font-bold tabular-nums"
                  style={{
                    color: tomorrow.projected_capital >= active_capital ? '#16C784' :
                           tomorrow.projected_capital < active_capital ? '#ef4444' : colors.accent,
                  }}
                >
                  {fmt(tomorrow.projected_capital)}
                </span>
              </div>
              {tomorrow.reasons && tomorrow.reasons.length > 0 && (
                <ul className="flex flex-col gap-0.5">
                  {tomorrow.reasons.slice(0, 4).map((r, i) => (
                    <li key={i} className="text-[10px] flex items-start gap-1" style={{ color: colors.secondaryText }}>
                      <span style={{ color: colors.accent }}>·</span>
                      {r}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Tier strip — scrolls on small screens instead of compressing */}
          <div className="overflow-x-auto -mx-3 px-3">
            <div className="flex items-center justify-between px-1 min-w-[420px]">
            {[
              { label: 'Min',        capital: '₹50K',  color: '#ef4444' },
              { label: 'Restricted', capital: '₹75K',  color: '#f97316' },
              { label: 'Standard',   capital: '₹1L',   color: '#16C784' },
              { label: 'Advanced',   capital: '₹1.5L', color: '#6366f1' },
              { label: 'Elite',      capital: '₹2L',   color: '#f59e0b' },
            ].map((t, i, arr) => {
              const active = tier === t.label || (tier === 'Minimum' && t.label === 'Min');
              return (
                <React.Fragment key={t.label}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[9px]" style={{ color: active ? t.color : colors.secondaryText, opacity: active ? 1 : 0.4, fontWeight: active ? 700 : 400 }}>
                      {t.capital}
                    </span>
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: t.color, opacity: active ? 1 : 0.3, boxShadow: active ? `0 0 6px ${t.color}` : 'none' }}
                    />
                    <span className="text-[9px]" style={{ color: active ? t.color : colors.secondaryText, opacity: active ? 1 : 0.4, fontWeight: active ? 700 : 400 }}>
                      {t.label}
                    </span>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex-1 h-px mx-1" style={{ backgroundColor: colors.border }} />
                  )}
                </React.Fragment>
              );
            })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
